/** Public product detail page (Bizcover India). */
export const PRODUCT_DETAIL_PATH_PREFIX = "/product";

/** Public seller/shop page (Bizcover India). */
export const SELLER_DETAIL_PATH_PREFIX = "/seller";

/** Seller dashboard segments — not public shop profile URLs. */
const SELLER_PRIVATE_PATH_SEGMENTS = new Set([
  "dashboard",
  "analytics",
  "enquiries",
  "rfq-inbox",
  "rfq",
  "quotes",
  "store_front",
  "products",
  "product_management",
  "create_product",
  "media_management",
  "profile",
  "membership_plans",
  "profile_settings",
]);

function getSellerPathSegment(pathname: string): string | null {
  const prefixes = ["/seller/", "/bizcover/seller/"];
  for (const prefix of prefixes) {
    if (pathname.startsWith(prefix)) {
      const segment = pathname.slice(prefix.length).split("/")[0]?.trim();
      return segment || null;
    }
  }
  if (pathname.startsWith("/seller_detail/")) {
    const segment = pathname.slice("/seller_detail/".length).split("/")[0]?.trim();
    return segment || null;
  }
  return null;
}

export function isPublicSellerDetailPath(pathname: string): boolean {
  const segment = getSellerPathSegment(pathname);
  return segment != null && !SELLER_PRIVATE_PATH_SEGMENTS.has(segment);
}

type SlugOrIdInput =
  | string
  | {
      id?: string | null;
      slug?: string | null;
    }
  | null
  | undefined;

function resolveSlugOrId(input: Exclude<SlugOrIdInput, null | undefined>): string {
  if (typeof input === "string") {
    return input.trim();
  }
  const slug = input.slug?.trim();
  if (slug) return slug;
  return String(input.id ?? "").trim();
}

/**
 * Prefer SEO slug when available; fall back to cuid for backward compatibility.
 */
export function getProductDetailPath(product: SlugOrIdInput): string {
  if (product == null) return PRODUCT_DETAIL_PATH_PREFIX;
  const key = resolveSlugOrId(product);
  return key ? `${PRODUCT_DETAIL_PATH_PREFIX}/${encodeURIComponent(key)}` : PRODUCT_DETAIL_PATH_PREFIX;
}

export function getProductDetailShareUrl(product: SlugOrIdInput): string {
  const path = getProductDetailPath(product);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export function isProductDetailPath(pathname: string): boolean {
  return pathname.startsWith("/product/") || pathname.startsWith("/bizcover/product/");
}

/**
 * Prefer company shop slug when available; fall back to seller profile id.
 */
export function getSellerDetailPath(
  sellerOrShop:
    | string
    | {
        id?: string | null;
        slug?: string | null;
        company?: { slug?: string | null } | null;
      }
    | null
    | undefined,
): string {
  if (sellerOrShop == null) return SELLER_DETAIL_PATH_PREFIX;
  if (typeof sellerOrShop === "string") {
    const key = sellerOrShop.trim();
    return key
      ? `${SELLER_DETAIL_PATH_PREFIX}/${encodeURIComponent(key)}`
      : SELLER_DETAIL_PATH_PREFIX;
  }
  const slug =
    sellerOrShop.slug?.trim() ||
    sellerOrShop.company?.slug?.trim() ||
    "";
  const key = slug || String(sellerOrShop.id ?? "").trim();
  return key
    ? `${SELLER_DETAIL_PATH_PREFIX}/${encodeURIComponent(key)}`
    : SELLER_DETAIL_PATH_PREFIX;
}

export function isSellerDetailPath(pathname: string): boolean {
  return isPublicSellerDetailPath(pathname);
}

/**
 * Update the browser URL to the canonical slug path without triggering
 * React Router remounts / re-fetches (avoids detail-page flicker).
 */
export function replaceCanonicalPath(canonicalPath: string): void {
  if (typeof window === "undefined") return;
  const next = canonicalPath.trim();
  if (!next) return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const nextFull = next.includes("?") || next.includes("#")
    ? next
    : `${next}${window.location.search}${window.location.hash}`;
  if (current === nextFull || window.location.pathname === next) return;
  window.history.replaceState(window.history.state, "", nextFull);
}
